import {
  cities,
  composers,
  orgs,
  meta,
  facetsLoaded,
  citiesLoaded,
  dataError,
  historicalCities,
  archiveSources,
  archiveSourcesLoaded,
  disabledSources,
  historyBoundsMinYear,
  timeFrom,
  timeWindowFromURL,
  timeWindowTouched,
  hintHistoryReplace,
} from "../state/store";
import type { City, Composer, Meta, Org, SourceInfo } from "../types";
import { yearOf } from "../utils/historicalDate";
import { buildSearchIndex } from "./search-index";
import { startQuerySync } from "./queries";

// Only the small aggregates are loaded up front; concert *records* are fetched on
// demand from the /api endpoints (see data/queries.ts). The aggregates are served
// from the Pages origin under /data/ — set VITE_DATA_URL to override (e.g. an R2
// base, trailing slash) when hosting them elsewhere.
// `||` (not `??`) so an empty VITE_DATA_URL — e.g. a blank line left in .env —
// falls back to the Pages-served /data/ path instead of resolving to "/".
const RAW_DATA_BASE = import.meta.env.VITE_DATA_URL || import.meta.env.BASE_URL + "data/";
const DATA_BASE = RAW_DATA_BASE.endsWith("/") ? RAW_DATA_BASE : RAW_DATA_BASE + "/";

async function fetchJSON<T>(filename: string): Promise<T> {
  const res = await fetch(DATA_BASE + filename);
  if (!res.ok) throw new Error(`Failed to load ${filename}: ${res.status}`);
  return res.json() as Promise<T>;
}

/**
 * Load the startup aggregates (cities + meta render the map immediately — their baked
 * counts stand in for the D1 counts until those arrive) and start the query sync.
 * composers.json/orgs.json (~125KB) are NOT loaded here: they only serve search,
 * chips, and Explore, so they load on first need via ensureFacets().
 */
export async function loadAllData(): Promise<void> {
  dataError.value = null;

  // Begin fetching concerts/counts immediately — they don't depend on the
  // aggregates, so the list and markers populate in parallel with this load.
  startQuerySync();

  // The Sources facet. Not fatal if it fails (an older deployment without the file just
  // shows the facet with only the live pseudo-source).
  //
  // ⚠⚠ Static, not `/api/historical-sources`. That endpoint GROUPed all 130,711
  // historical_concerts rows with a json_extract each to return 1.8 KB — measured 1.59 s of
  // D1, the single largest term in the landing page's cold cost, because the seven landing
  // calls serialize against one D1 database. The answer is deploy-time constant, so it is
  // exported to /data/ like cities/composers/orgs: CDN-cached globally, zero D1.
  fetchJSON<SourceInfo[]>("historical_sources.json")
    .then((sources) => {
      archiveSources.value = sources;
      archiveSourcesLoaded.value = true;
      expandDisabledPlaceholders();
    })
    .catch((err) => console.error("historical_sources.json failed", err));

  try {
    const [citiesData, metaData, historicalCitiesData] = await Promise.all([
      fetchJSON<City[]>("cities.json"),
      fetchJSON<Meta>("meta.json"),
      fetchJSON<City[]>("historical_cities.json").catch(() => [] as City[]),
    ]);
    cities.value = citiesData;
    meta.value = metaData;
    historicalCities.value = historicalCitiesData;
    citiesLoaded.value = true;

    // If the user beat cities.json to the search box, the index was built with an empty
    // city list — rebuild now that the real one is here.
    if (facetsLoaded.peek()) {
      buildSearchIndex(composers.peek(), citiesData, orgs.peek());
    }

    if (metaData.historical?.date_earliest) {
      const minYear = yearOf(metaData.historical.date_earliest);
      historyBoundsMinYear.value = minYear;
      // Apply the real full-span default unless a shared link or the user already
      // narrowed the window (url-sync runs before this; user edits can too).
      if (!timeWindowFromURL.value && !timeWindowTouched.value) {
        timeFrom.value = `${minYear}-01-01`;
      }
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    dataError.value =
      "Reference data files are missing or unreadable. Run `api export --output-dir web/public/data/` and reload the page. Details: " +
      message;
    throw err;
  }
}

/** Legacy URL params (`arch=0`, `lowconf=0`) park placeholders in `disabledSources`
 *  because the archive source keys aren't known until historical_sources.json loads.
 *  Expand them into explicit keys once it has, so the Sources UI and the `xsrc` query
 *  param only ever deal in real keys. */
function expandDisabledPlaceholders(): void {
  const disabled = disabledSources.value;
  if (!disabled.has("_archive") && !disabled.has("_probable")) return;
  const next = new Set(disabled);
  if (next.delete("_archive")) {
    for (const s of archiveSources.value) next.add(s.key);
  }
  if (next.delete("_probable")) {
    for (const s of archiveSources.value) if (s.status !== "confirmed") next.add(s.key);
  }
  hintHistoryReplace(); // canonicalization, not a user step — no history entry
  disabledSources.value = next;
}

// ── Deferred facet aggregates (composers + orgs) ───────────────────

let facetsPromise: Promise<void> | null = null;

/** Load composers.json + orgs.json and build the search index. Memoized; callers are
 *  the composer/org autocompletes (on first search), Explore (on mount), and url-sync
 *  (when a shared link carries ?composer=/?org= and the chip needs a name). */
export function ensureFacets(): Promise<void> {
  if (!facetsPromise) {
    facetsPromise = (async () => {
      const [composersData, orgsData] = await Promise.all([
        fetchJSON<Composer[]>("composers.json"),
        fetchJSON<Org[]>("orgs.json").catch(() => [] as Org[]),
      ]);
      composers.value = composersData;
      orgs.value = orgsData;
      buildSearchIndex(composersData, cities.peek(), orgsData);
      facetsLoaded.value = true;
    })().catch((err) => {
      facetsPromise = null; // allow a retry on the next interaction
      throw err;
    });
  }
  return facetsPromise;
}
